/**
 * Message templates view — Phase 2.3.j.2.
 *
 * Owns the "Message Templates" section of the Ticketing Configuration
 * page: filterable templates table (channel/event/locale), per-row
 * preview/test-send/enable-disable actions, and the create form. The
 * preview dialog renders a parameterized preview against canned
 * scenario data and supports a "test send" mode that posts the rendered
 * email/SMS to a real recipient.
 *
 * Like `ai-prompts.js`, this module is a sub-view of the broader
 * Channel Settings cluster — it shares `channelSettingsState` (still
 * owned by admin.js) via an injected getter so the slice it cares about
 * (`state.templates`, `state.loadingTemplates`) stays consistent across
 * the cluster.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from src/lib.
 */

import { formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { getChannelLabel } from '../../src/lib/channels.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- Constants ------------------------------------------------------
  const TEMPLATE_EVENT_LABELS = {
    message_received: 'Message received',
    technician_assigned: 'Technician assigned',
    technician_completed: 'Technician completed',
    ticket_closed: 'Ticket closed',
    clarification_low_confidence: 'Clarification · AI unsure',
    clarification_missing_info: 'Clarification · Missing info',
  };

  // configureChannelSettingsState wires the cross-view state lookup; kept
  // as a setter because the channel-settings view module owns the state.
  let getChannelSettingsState = () => ({ templates: [], loadingTemplates: false });

  function configureChannelSettingsState(getter) { getChannelSettingsState = getter; }

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let channelTemplatesTbody, channelTemplatesTable, refreshTemplatesBtn,
      templateFilterChannel, templateFilterEvent, templateFilterLocale,
      templateCreateForm, templateChannelInput, templateEventInput,
      templateLocaleInput, templateSubjectRow, templateSubjectInput,
      templateBodyInput, templateEnabledInput, templateCreateSubmit,
      templatePreviewDlg, templatePreviewForm, templatePreviewTitle,
      templatePreviewChannel, templatePreviewEvent, templatePreviewPlaceholders,
      templatePreviewContext, templatePreviewSubjectRow, templatePreviewSubject,
      templatePreviewBody, templateTestRecipientField, templateTestRecipientLabel,
      templateTestRecipient, templateTestRecipientHint, templatePreviewScenarioField,
      templatePreviewScenario, templateTestStatus, templateTestSendBtn;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    channelTemplatesTbody = document.getElementById('channelTemplatesTbody');
    channelTemplatesTable = document.getElementById('channelTemplatesTable');
    refreshTemplatesBtn = document.getElementById('refreshTemplatesBtn');
    templateFilterChannel = document.getElementById('templateFilterChannel');
    templateFilterEvent = document.getElementById('templateFilterEvent');
    templateFilterLocale = document.getElementById('templateFilterLocale');
    templateCreateForm = document.getElementById('templateCreateForm');
    templateChannelInput = document.getElementById('templateChannel');
    templateEventInput = document.getElementById('templateEvent');
    templateLocaleInput = document.getElementById('templateLocale');
    templateSubjectRow = document.getElementById('templateSubjectRow');
    templateSubjectInput = document.getElementById('templateSubject');
    templateBodyInput = document.getElementById('templateBody');
    templateEnabledInput = document.getElementById('templateEnabled');
    templateCreateSubmit = document.getElementById('templateCreateSubmit');
    templatePreviewDlg = document.getElementById('templatePreviewDlg');
    templatePreviewForm = document.getElementById('templatePreviewForm');
    templatePreviewTitle = document.getElementById('templatePreviewTitle');
    templatePreviewChannel = document.getElementById('templatePreviewChannel');
    templatePreviewEvent = document.getElementById('templatePreviewEvent');
    templatePreviewPlaceholders = document.getElementById('templatePreviewPlaceholders');
    templatePreviewContext = document.getElementById('templatePreviewContext');
    templatePreviewSubjectRow = document.getElementById('templatePreviewSubjectRow');
    templatePreviewSubject = document.getElementById('templatePreviewSubject');
    templatePreviewBody = document.getElementById('templatePreviewBody');
    templateTestRecipientField = document.getElementById('templateTestRecipientField');
    templateTestRecipientLabel = document.getElementById('templateTestRecipientLabel');
    templateTestRecipient = document.getElementById('templateTestRecipient');
    templateTestRecipientHint = document.getElementById('templateTestRecipientHint');
    templatePreviewScenarioField = document.getElementById('templatePreviewScenarioField');
    templatePreviewScenario = document.getElementById('templatePreviewScenario');
    templateTestStatus = document.getElementById('templateTestStatus');
    templateTestSendBtn = document.getElementById('templateTestSendBtn');
  }

  // ---- State (verbatim from admin.js) ---------------------------------
  const templateFilters = { channel: '', event: '', locale: '' };
  let templateFilterTimer = null;
  const templatePreviewState = {
    templateId: null,
    template: null,
    scenario: 'default',
    scenarios: [],
    overrides: {},
    loading: false,
    requestId: 0,
    focusAfterLoad: false,
    latestPreview: null,
  };

  // Compat aliases — the lifted function bodies reference
  // `channelSettingsState` directly. We point that name at our shim that
  // reads through to the admin.js-owned state.
  const channelSettingsState = new Proxy({}, {
    get(_, prop) { return getChannelSettingsState()[prop]; },
    set(_, prop, value) { getChannelSettingsState()[prop] = value; return true; },
  });

    async function loadMessageTemplates() {
      if (!channelTemplatesTbody) { console.warn('message-templates: #channelTemplatesTbody missing — templates will not load.'); return []; }
      if (templateFilterTimer) {
        clearTimeout(templateFilterTimer);
        templateFilterTimer = null;
      }
      channelSettingsState.loadingTemplates = true;
      renderMessageTemplates();
      try {
        const data = await adminAPI.fetch(API.adminChannelTemplates({
          channel: templateFilters.channel || undefined,
          event: templateFilters.event || undefined,
          locale: templateFilters.locale || undefined,
          includeDisabled: true,
        }), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        channelSettingsState.templates = items;
      } catch (err) {
        showFlash(err.message || 'Failed to load templates', 'error');
        channelSettingsState.templates = [];
      } finally {
        channelSettingsState.loadingTemplates = false;
        renderMessageTemplates();
      }
      return channelSettingsState.templates;
    }

    function renderMessageTemplates() {
      if (!channelTemplatesTbody) return;
      channelTemplatesTbody.innerHTML = '';
      if (channelSettingsState.loadingTemplates) {
        channelTemplatesTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading templates…</td></tr>';
        return;
      }
      const items = Array.isArray(channelSettingsState.templates) ? channelSettingsState.templates : [];
      if (!items.length) {
        channelTemplatesTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No templates found for the selected filters.</td></tr>';
        return;
      }
      items.forEach((item, index) => {
        const tr = document.createElement('tr');
        tr.dataset.templateId = String(item.id);
        const cells = [
          index + 1,
          getChannelLabel(item.channel),
          getEventLabel(item.event),
          item.locale || '—',
          item.version != null ? item.version : '—',
          item.enabled ? 'Enabled' : 'Disabled',
          formatDateTimeFull(item.updated_at || item.created_at),
        ];
        cells.forEach((value) => {
          const td = document.createElement('td');
          td.textContent = typeof value === 'string' ? value : String(value);
          tr.appendChild(td);
        });
        const actionTd = document.createElement('td');
        const previewBtn = document.createElement('button');
        previewBtn.type = 'button';
        previewBtn.className = 'btn ghost';
        previewBtn.dataset.templatePreview = String(item.id);
        previewBtn.textContent = 'Preview';
        previewBtn.style.marginRight = '6px';
        actionTd.appendChild(previewBtn);

        const testBtn = document.createElement('button');
        testBtn.type = 'button';
        testBtn.className = 'btn';
        testBtn.dataset.templateTest = String(item.id);
        testBtn.textContent = 'Test send';
        testBtn.style.marginRight = '6px';
        actionTd.appendChild(testBtn);

        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = item.enabled ? 'btn warn' : 'btn';
        toggleBtn.dataset.templateToggle = String(item.id);
        toggleBtn.dataset.enabled = item.enabled ? '1' : '0';
        toggleBtn.textContent = item.enabled ? 'Disable' : 'Enable';
        actionTd.appendChild(toggleBtn);
        tr.appendChild(actionTd);
        channelTemplatesTbody.appendChild(tr);
      });
    }

    function handleTemplateTableClick(event) {
      const previewTrigger = event.target.closest('button[data-template-preview]');
      if (previewTrigger) {
        const templateId = previewTrigger.dataset.templatePreview;
        if (templateId) {
          openTemplatePreview(templateId, { focusTest: false }).catch((err) => {
            console.warn('Failed to open template preview', err);
          });
        }
        return;
      }

      const testTrigger = event.target.closest('button[data-template-test]');
      if (testTrigger) {
        const templateId = testTrigger.dataset.templateTest;
        if (templateId) {
          openTemplatePreview(templateId, { focusTest: true }).catch((err) => {
            console.warn('Failed to open template test dialog', err);
          });
        }
        return;
      }

      const toggleBtn = event.target.closest('button[data-template-toggle]');
      if (!toggleBtn) return;
      const templateId = toggleBtn.dataset.templateToggle;
      if (!templateId) return;
      const enable = toggleBtn.dataset.enabled !== '1';
      toggleMessageTemplate(templateId, enable, toggleBtn);
    }

    async function toggleMessageTemplate(templateId, enable, btn) {
      const targetBtn = btn || document.querySelector(`button[data-template-toggle="${templateId}"]`);
      if (targetBtn) {
        targetBtn.disabled = true;
        targetBtn.dataset.originalText = targetBtn.textContent;
        targetBtn.textContent = enable ? 'Enabling…' : 'Disabling…';
      }
      try {
        await adminAPI.fetch(API.adminChannelTemplateEnable(templateId), {
          method: 'POST',
          body: JSON.stringify({ enabled: enable }),
        });
        showFlash(`Template ${enable ? 'enabled' : 'disabled'}.`);
        await loadMessageTemplates();
      } catch (err) {
        showFlash(err.message || 'Failed to update template', 'error');
      } finally {
        if (targetBtn) {
          targetBtn.disabled = false;
          targetBtn.textContent = targetBtn.dataset.originalText || (targetBtn.dataset.enabled === '1' ? 'Disable' : 'Enable');
          delete targetBtn.dataset.originalText;
        }
      }
    }

    function findTemplateById(templateId) {
      const templates = Array.isArray(channelSettingsState.templates) ? channelSettingsState.templates : [];
      return templates.find((item) => String(item.id) === String(templateId));
    }

    function getTemplateScenarioOptions(template) {
      const options = [{ value: 'default', label: 'Default sample', overrides: {} }];
      if (!template) return options;
      const eventName = String(template.event || '').toLowerCase();
      if (eventName === 'ticket_closed') {
        options.push(
          {
            value: 'resolved',
            label: 'Closure – resolved',
            overrides: {
              closure_reason: 'we resolved the issue after replacing your router',
            },
          },
          {
            value: 'unreachable',
            label: 'Closure – unreachable',
            overrides: {
              closure_reason: 'we could not reach you after multiple attempts',
            },
          },
        );
      }
      return options;
    }

    function setTemplateTestStatus(level, message) {
      if (!templateTestStatus) return;
      templateTestStatus.className = 'template-test-status';
      if (level) templateTestStatus.classList.add(level);
      templateTestStatus.textContent = message || '';
    }

    function populateTemplatePreviewPlaceholders(placeholders) {
      if (!templatePreviewPlaceholders) return;
      templatePreviewPlaceholders.innerHTML = '';
      const list = Array.isArray(placeholders) ? placeholders : [];
      if (!list.length) {
        const li = document.createElement('li');
        li.textContent = '(none)';
        templatePreviewPlaceholders.appendChild(li);
        return;
      }
      list.forEach((name) => {
        const li = document.createElement('li');
        li.textContent = `{{${name}}}`;
        templatePreviewPlaceholders.appendChild(li);
      });
    }

    function populateTemplatePreviewContext(context) {
      if (!templatePreviewContext) return;
      templatePreviewContext.innerHTML = '';
      const entries = Object.entries(context || {}).sort(([a], [b]) => a.localeCompare(b));
      if (!entries.length) {
        const dd = document.createElement('dd');
        dd.textContent = 'No sample context';
        dd.style.color = 'var(--sub)';
        templatePreviewContext.appendChild(dd);
        return;
      }
      entries.forEach(([key, rawValue]) => {
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        let value = rawValue;
        if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'object') {
          try {
            value = JSON.stringify(value);
          } catch {
            value = String(value);
          }
        }
        dd.textContent = String(value);
        templatePreviewContext.appendChild(dt);
        templatePreviewContext.appendChild(dd);
      });
    }

    function configureTemplateTestUI(template) {
      if (!templateTestSendBtn || !templateTestRecipientField || !templateTestRecipientLabel || !templateTestRecipientHint) return;
      const channel = String(template?.channel || '').toLowerCase();
      if (templateTestRecipient) templateTestRecipient.value = '';
      let buttonLabel = 'Send test';
      let fieldDisplay = 'none';
      let label = 'Recipient';
      let placeholder = '';
      let hint = '';
      let disableButton = false;

      switch (channel) {
        case 'email':
          fieldDisplay = 'grid';
          label = 'Recipient email';
          placeholder = 'customer@example.com';
          hint = 'Sends a plain text email using the configured SMTP settings.';
          disableButton = false;
          break;
        case 'whatsapp':
          fieldDisplay = 'grid';
          label = 'Recipient phone (WhatsApp)';
          placeholder = '+221770001234';
          hint = 'Enter an international phone number reachable on WhatsApp.';
          disableButton = false;
          break;
        case 'app':
          fieldDisplay = 'none';
          buttonLabel = 'Record preview';
          hint = 'App acknowledgements are preview-only; no recipient required.';
          disableButton = false;
          break;
        default:
          fieldDisplay = 'none';
          hint = 'Test send is unavailable for this channel.';
          disableButton = true;
          break;
      }

      templateTestRecipientField.style.display = fieldDisplay;
      templateTestRecipientLabel.textContent = label;
      if (templateTestRecipient) templateTestRecipient.placeholder = placeholder;
      templateTestRecipientHint.textContent = hint;
      templateTestSendBtn.textContent = buttonLabel;
      templateTestSendBtn.disabled = disableButton;
      templateTestSendBtn.dataset.channel = channel;
      if (channel !== 'app') {
        setTemplateTestStatus('', '');
      } else if (!templatePreviewState.latestPreview) {
        setTemplateTestStatus('warn', 'INET-Go acknowledgements are preview-only; use the preview text for QA.');
      }
    }

    function applyTemplatePreviewResponse(data) {
      if (!data) return;
      templatePreviewState.latestPreview = data;
      populateTemplatePreviewPlaceholders(data.placeholders || []);
      populateTemplatePreviewContext(data.context || {});
      if (templatePreviewSubjectRow) {
        const hasSubject = Boolean(data.rendered_subject);
        templatePreviewSubjectRow.style.display = hasSubject ? 'grid' : 'none';
        if (templatePreviewSubject) {
          templatePreviewSubject.textContent = hasSubject ? data.rendered_subject : '(no subject)';
        }
      }
      if (templatePreviewBody) {
        templatePreviewBody.textContent = data.rendered_body || '(no body)';
      }
    }

    async function openTemplatePreview(templateId, opts = {}) {
      const template = findTemplateById(templateId);
      if (!template) {
        showFlash('Template not found. Reload the list and try again.', 'error');
        return;
      }
      templatePreviewState.templateId = templateId;
      templatePreviewState.template = template;
      templatePreviewState.scenarios = getTemplateScenarioOptions(template);
      templatePreviewState.scenario = templatePreviewState.scenarios[0]?.value || 'default';
      templatePreviewState.overrides = {};
      templatePreviewState.focusAfterLoad = Boolean(opts && opts.focusTest);
      templatePreviewState.latestPreview = null;

      if (templatePreviewScenario) {
        templatePreviewScenario.innerHTML = '';
        templatePreviewState.scenarios.forEach((option) => {
          const opt = document.createElement('option');
          opt.value = option.value;
          opt.textContent = option.label;
          templatePreviewScenario.appendChild(opt);
        });
        templatePreviewScenario.value = templatePreviewState.scenario;
      }
      if (templatePreviewScenarioField) {
        templatePreviewScenarioField.style.display = templatePreviewState.scenarios.length > 1 ? 'grid' : 'none';
      }

      if (templatePreviewTitle) {
        templatePreviewTitle.textContent = `Template preview – ${getChannelLabel(template.channel)} / ${getEventLabel(template.event)}`;
      }
      if (templatePreviewChannel) {
        templatePreviewChannel.textContent = getChannelLabel(template.channel);
      }
      if (templatePreviewEvent) {
        templatePreviewEvent.textContent = getEventLabel(template.event);
      }
      if (templatePreviewSubjectRow) {
        templatePreviewSubjectRow.style.display = String(template.channel || '').toLowerCase() === 'email' ? 'grid' : 'none';
      }
      if (templatePreviewSubject) {
        templatePreviewSubject.textContent = 'Loading preview…';
      }
      if (templatePreviewBody) {
        templatePreviewBody.textContent = 'Loading preview…';
      }
      populateTemplatePreviewPlaceholders([]);
      populateTemplatePreviewContext({});
      setTemplateTestStatus('', '');

      configureTemplateTestUI(template);

      if (templatePreviewDlg) {
        openDialog(templatePreviewDlg);
      }

      await loadTemplatePreview();
    }

    async function loadTemplatePreview() {
      if (!templatePreviewState.templateId) return;
      if (!templatePreviewState.scenarios.length) return;
      const scenario = templatePreviewState.scenarios.find((item) => item.value === templatePreviewState.scenario) || templatePreviewState.scenarios[0];
      const overrides = Object.assign({}, scenario ? scenario.overrides : {});
      templatePreviewState.overrides = overrides;
      templatePreviewState.loading = true;
      const requestId = templatePreviewState.requestId + 1;
      templatePreviewState.requestId = requestId;

      if (templateTestSendBtn) {
        templateTestSendBtn.disabled = true;
        templateTestSendBtn.dataset.loadingLabel = templateTestSendBtn.textContent;
        templateTestSendBtn.textContent = 'Loading…';
      }

      try {
        const response = await adminAPI.fetch(API.adminChannelTemplatePreview(templatePreviewState.templateId), {
          method: 'POST',
          body: JSON.stringify({ context_overrides: overrides }),
        });
        if (requestId !== templatePreviewState.requestId) return;
        applyTemplatePreviewResponse(response);
        const channel = String(templatePreviewState.template?.channel || '').toLowerCase();
        if (channel === 'app' && (!response || !response.detail)) {
          setTemplateTestStatus('warn', 'INET-Go acknowledgements are preview-only; use the preview text for QA.');
        }
      } catch (err) {
        if (requestId === templatePreviewState.requestId) {
          applyTemplatePreviewResponse({ rendered_body: '', rendered_subject: '', placeholders: [], context: {} });
          setTemplateTestStatus('error', err.message || 'Failed to load template preview.');
        }
      } finally {
        if (requestId === templatePreviewState.requestId) {
          templatePreviewState.loading = false;
          if (templateTestSendBtn) {
            const channel = templateTestSendBtn.dataset.channel || '';
            const originalLabel = templateTestSendBtn.dataset.loadingLabel;
            templateTestSendBtn.textContent = originalLabel || (channel === 'app' ? 'Record preview' : 'Send test');
            templateTestSendBtn.disabled = channel === 'app' ? false : (channel === '' ? true : false);
            delete templateTestSendBtn.dataset.loadingLabel;
          }
          if (templatePreviewState.focusAfterLoad && templateTestRecipient && templateTestRecipientField && templateTestRecipientField.style.display !== 'none') {
            templatePreviewState.focusAfterLoad = false;
            templateTestRecipient.focus();
          }
        }
      }
    }

    function handleTemplateScenarioChange() {
      if (!templatePreviewScenario) return;
      templatePreviewState.scenario = templatePreviewScenario.value || 'default';
      loadTemplatePreview().catch((err) => {
        console.warn('Failed to refresh template preview', err);
      });
    }

    async function handleTemplateTestSend(event) {
      event.preventDefault();
      if (!templatePreviewState.templateId || !templatePreviewState.template || !templateTestSendBtn) return;
      if (templatePreviewState.loading) return;
      const channel = templateTestSendBtn.dataset.channel || '';
      let recipient = templateTestRecipient ? templateTestRecipient.value.trim() : '';

      if (channel === 'email' && !recipient) {
        setTemplateTestStatus('error', 'Enter an email address to send the test message.');
        if (templateTestRecipient) templateTestRecipient.focus();
        return;
      }
      if (channel === 'whatsapp' && !recipient) {
        setTemplateTestStatus('error', 'Enter a WhatsApp phone number to send the test message.');
        if (templateTestRecipient) templateTestRecipient.focus();
        return;
      }
      if (channel !== 'email' && channel !== 'whatsapp' && channel !== 'app') {
        setTemplateTestStatus('error', 'Test send unsupported for this channel.');
        return;
      }

      const originalText = templateTestSendBtn.textContent;
      templateTestSendBtn.disabled = true;
      templateTestSendBtn.textContent = 'Sending…';
      setTemplateTestStatus('', '');

      try {
        const payload = {
          recipient: recipient || null,
          context_overrides: templatePreviewState.overrides || {},
        };
        const result = await adminAPI.fetch(API.adminChannelTemplateTestSend(templatePreviewState.templateId), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        applyTemplatePreviewResponse(result);
        let level = 'warn';
        if (result.status === 'sent') level = 'success';
        else if (result.status === 'failed') level = 'error';
        else if (result.status === 'skipped') level = 'warn';
        const detailMessage = result.detail || `Test ${result.status}`;
        const recipientMessage = result.recipient ? ` → ${result.recipient}` : '';
        setTemplateTestStatus(level, `${detailMessage}${recipientMessage}`);
      } catch (err) {
        setTemplateTestStatus('error', err.message || 'Failed to send test message.');
      } finally {
        templateTestSendBtn.disabled = false;
        templateTestSendBtn.textContent = originalText || 'Send test';
      }
    }

    function resetTemplatePreviewDialog() {
      templatePreviewState.templateId = null;
      templatePreviewState.template = null;
      templatePreviewState.scenario = 'default';
      templatePreviewState.scenarios = [];
      templatePreviewState.overrides = {};
      templatePreviewState.latestPreview = null;
      templatePreviewState.focusAfterLoad = false;
      if (templateTestRecipient) templateTestRecipient.value = '';
      setTemplateTestStatus('', '');
    }

    function handleTemplateFilterChange() {
      if (templateFilterTimer) {
        clearTimeout(templateFilterTimer);
        templateFilterTimer = null;
      }
      templateFilters.channel = templateFilterChannel ? templateFilterChannel.value : '';
      templateFilters.event = templateFilterEvent ? templateFilterEvent.value : '';
      templateFilters.locale = templateFilterLocale ? templateFilterLocale.value.trim() : '';
      loadMessageTemplates().catch(() => {});
    }

    function handleTemplateFilterInput() {
      if (!templateFilterLocale) return;
      if (templateFilterTimer) {
        clearTimeout(templateFilterTimer);
      }
      templateFilterTimer = setTimeout(() => {
        templateFilters.locale = templateFilterLocale.value.trim();
        loadMessageTemplates().catch(() => {});
      }, 400);
    }

    function getEventLabel(eventName) {
      return TEMPLATE_EVENT_LABELS[eventName] || (eventName || 'Event');
    }

    function updateTemplateSubjectVisibility() {
      if (!templateSubjectRow) return;
      const channel = templateChannelInput ? templateChannelInput.value : 'email';
      const isEmail = channel === 'email';
      templateSubjectRow.style.display = isEmail ? 'flex' : 'none';
      if (!isEmail && templateSubjectInput) {
        templateSubjectInput.value = '';
      }
    }

    async function submitTemplateCreate(event) {
      event.preventDefault();
      if (!templateCreateForm) return;
      if (templateCreateForm.dataset.pending === '1') return;
      if (!templateChannelInput || !templateEventInput || !templateLocaleInput || !templateBodyInput) return;
      const channel = templateChannelInput.value;
      const eventName = templateEventInput.value;
      if (!channel || !eventName) {
        showFlash('Select channel and event', 'error');
        return;
      }
      const locale = (templateLocaleInput.value || 'en').trim() || 'en';
      const body = templateBodyInput.value || '';
      if (!body.trim()) {
        showFlash('Template body is required', 'error');
        return;
      }
      const payload = {
        channel,
        event: eventName,
        locale,
        body_template: body,
        enabled: templateEnabledInput ? Boolean(templateEnabledInput.checked) : true,
      };
      if (channel === 'email') {
        payload.subject_template = templateSubjectInput ? (templateSubjectInput.value || '').trim() || null : null;
      } else {
        payload.subject_template = null;
      }
      templateCreateForm.dataset.pending = '1';
      if (templateCreateSubmit) {
        templateCreateSubmit.disabled = true;
        templateCreateSubmit.textContent = 'Creating…';
      }
      try {
        await adminAPI.fetch(API.adminChannelTemplateCreate, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showFlash('Template created');
        resetTemplateCreateForm();
        await loadMessageTemplates();
      } catch (err) {
        showFlash(err.message || 'Failed to create template', 'error');
      } finally {
        templateCreateForm.dataset.pending = '0';
        if (templateCreateSubmit) {
          templateCreateSubmit.disabled = false;
          templateCreateSubmit.textContent = 'Create template';
        }
      }
    }

    function resetTemplateCreateForm() {
      if (!templateCreateForm) return;
      if (templateChannelInput) templateChannelInput.value = 'email';
      if (templateEventInput) templateEventInput.value = 'message_received';
      if (templateLocaleInput) templateLocaleInput.value = 'en';
      if (templateSubjectInput) templateSubjectInput.value = '';
      if (templateBodyInput) templateBodyInput.value = '';
      if (templateEnabledInput) templateEnabledInput.checked = true;
      updateTemplateSubjectVisibility();
    }

  function attachListeners() {
    resolveDom();
    if (refreshTemplatesBtn) {
      refreshTemplatesBtn.addEventListener('click', () => {
        loadMessageTemplates().catch(() => {});
      });
    }
    if (channelTemplatesTable) {
      channelTemplatesTable.addEventListener('click', handleTemplateTableClick);
    }
    if (templateCreateForm) {
      templateCreateForm.addEventListener('submit', submitTemplateCreate);
    }
    if (templateFilterChannel) {
      templateFilterChannel.addEventListener('change', handleTemplateFilterChange);
    }
    if (templateFilterEvent) {
      templateFilterEvent.addEventListener('change', handleTemplateFilterChange);
    }
    if (templateFilterLocale) {
      templateFilterLocale.addEventListener('input', handleTemplateFilterInput);
    }
    if (templateChannelInput) {
      templateChannelInput.addEventListener('change', updateTemplateSubjectVisibility);
    }
    if (templatePreviewForm) {
      templatePreviewForm.addEventListener('submit', handleTemplateTestSend);
    }
    if (templatePreviewScenario) {
      templatePreviewScenario.addEventListener('change', handleTemplateScenarioChange);
    }
    if (templatePreviewDlg) {
      templatePreviewDlg.addEventListener('close', resetTemplatePreviewDialog);
    }
    if (templateFilterChannel) templateFilterChannel.value = templateFilters.channel;
    if (templateFilterEvent) templateFilterEvent.value = templateFilters.event;
    if (templateFilterLocale) templateFilterLocale.value = templateFilters.locale;
    updateTemplateSubjectVisibility();
  }

  export default {
    configureChannelSettingsState,
    attachListeners,
    init: () => { resolveDom(); loadMessageTemplates(); },
    loadMessageTemplates,
    renderMessageTemplates,
    getEventLabel,
  };
